import "./TodaysCurrency.css";
import {
  convertCurrency,
  resolveCurrencyQuery,
  supportedCurrencies,
} from "./currency/CurrencyApi";
import {
  escapeCurrencyHtml as esc,
  formatCurrencyNumber,
  formatProviderTime,
  type CurrencyResult,
  type SupportedCurrency,
} from "./currency/CurrencyTypes";
import { looksLikeCurrencyConversionQuery, looksLikeExplicitCurrencyAmount } from "./CurrencyConverter";

export function looksLikeTodaysCurrencyQuery(query: string): boolean {
  const q = String(query || "").trim();

  const naturalCountryCurrencyToday =
    /\b(?:china|china's|chinas|japan|japan's|japans|nigeria|nigeria's|nigerias|ghana|kenya|south\s+africa|america|united\s+states|britain|england)\s+(?:money|currency)\b/i.test(q) &&
    /\b(?:today|current|currently|price|rate|exchange\s+rate|equals?)\b/i.test(q);

  if (!looksLikeCurrencyConversionQuery(q) && !naturalCountryCurrencyToday) {
    return false;
  }

  if (looksLikeExplicitCurrencyAmount(q)) return false;

  return /\b(?:today|current|currently|price|rate|exchange\s+rate|equals?)\b/i.test(q);
}

function options(currencies: SupportedCurrency[], selected: string): string {
  return currencies
    .map(
      (item) =>
        `<option value="${esc(item.code)}" ${item.code === selected ? "selected" : ""}>${esc(item.name)} (${esc(item.code)})</option>`,
    )
    .join("");
}

function resultHtml(data: CurrencyResult): string {
  const updated = formatProviderTime(data.provider_updated_at);
  const source = data.source;
  const sourceLabel = source?.title || source?.name || "Exchange-rate source";

  return `
    <div class="today-currency-equation">
      <span>1 ${esc(data.from.name)}</span>
      <small>equals</small>
      <strong>${esc(formatCurrencyNumber(data.rate))} ${esc(data.to.name)}</strong>
    </div>

    ${
      data.ngn_reference
        ? `<div class="today-currency-ngn">1 ${esc(data.from.code)} ≈ ${esc(formatCurrencyNumber(data.ngn_reference.rate))} NGN</div>`
        : ""
    }

    <div class="today-currency-meta">
      <span>${updated ? esc(updated) : "Latest available cached rate"}</span>
      ${
        source?.url
          ? `<span>· From <a href="${esc(source.url)}" target="_blank" rel="noopener noreferrer">${esc(sourceLabel)}</a></span>`
          : ""
      }
      <span>· Indicative rate</span>
    </div>
  `;
}


async function todayCurrencyList(
  data: CurrencyResult,
): Promise<SupportedCurrency[]> {
  const currencies = await supportedCurrencies().catch(() => []);
  return currencies.length > 0
    ? currencies
    : [
        { code: data.from.code, name: data.from.name },
        { code: data.to.code, name: data.to.name },
      ];
}

function todayCurrencyCardMarkup(
  data: CurrencyResult,
  list: SupportedCurrency[],
): string {
  return `
    <section class="today-currency-card" aria-label="Today's currency rate">
      <div class="today-currency-controls">
        <select data-today-from aria-label="Source currency">
          ${options(list, data.from.code)}
        </select>
        <span>to</span>
        <select data-today-to aria-label="Target currency">
          ${options(list, data.to.code)}
        </select>
      </div>

      <div data-today-currency-result>
        ${resultHtml(data)}
      </div>
    </section>
  `;
}

function attachTodayCurrencyEvents(container: HTMLElement): void {
  const from = container.querySelector<HTMLSelectElement>("[data-today-from]");
  const to = container.querySelector<HTMLSelectElement>("[data-today-to]");
  const host = container.querySelector<HTMLElement>("[data-today-currency-result]");

  const refresh = async () => {
    if (!from || !to || !host) return;
    host.classList.add("is-loading");
    try {
      const next = await convertCurrency(1, from.value, to.value);
      host.innerHTML = resultHtml(next);
    } catch (error) {
      console.error("Today's currency refresh failed:", error);
    } finally {
      host.classList.remove("is-loading");
    }
  };

  from?.addEventListener("change", refresh);
  to?.addEventListener("change", refresh);
}

/**
 * Mount the existing today's-rate card from AI Overview's deterministic payload.
 */
export async function mountTodaysCurrencyData(
  container: HTMLElement,
  data: CurrencyResult,
): Promise<void> {
  const list = await todayCurrencyList(data);
  container.innerHTML = todayCurrencyCardMarkup(data, list);
  attachTodayCurrencyEvents(container);
}

export async function renderTodaysCurrency(
  container: HTMLElement,
  query: string,
): Promise<boolean> {
  container.innerHTML = `<section class="today-currency-card">Loading latest currency rate…</section>`;

  try {
    const data = await resolveCurrencyQuery(query);

    await mountTodaysCurrencyData(container, data);

    return true;
  } catch (error) {
    console.error("Today's currency card failed:", error);
    container.innerHTML = "";
    return false;
  }
}
